import React from 'react'
import { Card, Row, Col, Statistic, Table, Tag, Button, message, Modal, Form, Input, Select, Space } from 'antd'
import { UserOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import axios from 'axios'

const { Option } = Select

function AdminDashboard() {
  const [stats, setStats] = React.useState({})
  const [users, setUsers] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [userModalVisible, setUserModalVisible] = React.useState(false)
  const [createUserModalVisible, setCreateUserModalVisible] = React.useState(false)
  const [editingUser, setEditingUser] = React.useState(null)
  const [form] = Form.useForm()
  const [createForm] = Form.useForm()

  // 获取管理员统计数据
  const fetchAdminStats = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/admin/stats')
      setStats(response.data)
    } catch (error) {
      message.error('获取统计数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 获取所有用户列表
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users')
      setUsers(response.data)
    } catch (error) {
      message.error('获取用户列表失败')
    }
  }

  React.useEffect(() => {
    fetchAdminStats()
    fetchAllUsers()
  }, [])

  // 用户表格列配置
  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role === 'admin' ? '管理员' : '业务员'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '停用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEditUser(record)}>
            编辑
          </Button>
          <Button 
            type="link" 
            danger
            onClick={() => handleDeleteUser(record._id)}
            disabled={record.role === 'admin'}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ]

  // 编辑用户
  const handleEditUser = (user) => {
    setEditingUser(user)
    form.setFieldsValue({
      display_name: user.display_name || '',
      role: user.role,
      status: user.status
    })
    setUserModalVisible(true)
  }

  // 保存用户编辑
  const handleSaveUser = async (values) => {
    try {
      await axios.put(`/api/admin/users/${editingUser._id}`, values)
      message.success('用户信息更新成功')
      setUserModalVisible(false)
      setEditingUser(null)
      form.resetFields()
      fetchAllUsers()
    } catch (error) {
      message.error('更新失败')
    }
  }

  // 删除用户
  const handleDeleteUser = async (userId) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个用户吗？此操作不可恢复。',
      onOk: async () => {
        try {
          await axios.delete(`/api/admin/users/${userId}`)
          message.success('用户删除成功')
          fetchAllUsers()
        } catch (error) {
          message.error('删除失败')
        }
      }
    })
  }

  // 创建新用户
  const handleCreateUser = async (values) => {
    try {
      await axios.post('/api/admin/users', values)
      message.success('用户创建成功')
      setCreateUserModalVisible(false)
      createForm.resetFields()
      fetchAllUsers()
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error)
      } else {
        message.error('创建失败')
      }
    }
  }

  return (
    <div className="admin-dashboard">
      <h2>管理员控制台</h2>
      
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总用户数"
              value={stats.total_users || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃用户"
              value={stats.active_users || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总客户数"
              value={stats.total_customers || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日完成"
              value={stats.today_completed || 0}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 业务员业绩统计 */}
      {stats.sales_stats && stats.sales_stats.length > 0 && (
        <Card title="业务员今日业绩" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            {stats.sales_stats.map((sales, index) => (
              <Col span={8} key={index}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <h4>{sales.salesman_name}</h4>
                    <p>今日完成：<strong>{sales.today_completed}</strong> 单</p>
                    <p>累计完成：<strong>{sales.total_completed}</strong> 单</p>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* 用户管理 */}
      <Card 
        title="用户管理"
        extra={
          <Button 
            type="primary" 
            icon={<UserOutlined />}
            onClick={() => setCreateUserModalVisible(true)}
          >
            开通新用户
          </Button>
        }
      >
        <Table
          columns={userColumns}
          dataSource={users}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户"
        open={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false)
          setEditingUser(null)
          form.resetFields()
        }}
        footer={null}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveUser}
        >
          <Form.Item
            name="display_name"
            label="显示名称"
          >
            <Input placeholder="请输入显示名称" />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Option value="user">业务员</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="active">活跃</Option>
              <Option value="inactive">停用</Option>
            </Select>
          </Form.Item>
          
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Button 
              onClick={() => {
                setUserModalVisible(false)
                setEditingUser(null)
                form.resetFields()
              }}
              style={{ marginRight: 8 }}
            >
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建新用户模态框 */}
      <Modal
        title="开通新用户"
        open={createUserModalVisible}
        onCancel={() => {
          setCreateUserModalVisible(false)
          createForm.resetFields()
        }}
        footer={null}
        width={500}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' }
            ]}
          >
            <Input placeholder="请输入用户名（员工登录账号）" />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入密码（将下发给员工）" />
          </Form.Item>
          
          <Form.Item
            name="display_name"
            label="显示名称"
          >
            <Input placeholder="请输入员工姓名（可选）" />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Option value="user">业务员</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>
          
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Button 
              onClick={() => {
                setCreateUserModalVisible(false)
                createForm.resetFields()
              }}
              style={{ marginRight: 8 }}
            >
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              创建用户
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminDashboard